// §4 S2 — Operations tab (Module 04, slice 04-A). Step list with per-op
// status, quantities, planned vs actual minutes, skip_reason when skipped, and
// the two 04-A actions: Skip (§5 row 9) and Record result (§5 row 34, §3.11 —
// the paper-traveler path). Both actions live inside `.wo-transitions`, so a
// phone reads the table and changes nothing (§4 mobile behavior). Both are
// ALSO gated on the work order's own status by the caller (spec §4 S2 note,
// 2026-08-14) — a cancelled/closed order renders no actions column at all.
//
// THE RECORDED-RESULTS PANEL is §3.11's "the WO detail shows which results
// were keyed and by whom": every §2.4 report with its `capture_source` +
// `reported_at`. "The tablet said 5980" and "someone typed 5980" are never the
// same fact, so the two sources are named differently on sight. The principal
// is the bare `worker_id` uuid — deliberately NEVER resolved to a name (spec
// §5 row 3: no universally-held members endpoint exists to resolve one).
//
// After a successful result the caller must NOT reason from the pre-mutation
// status: the first result flips released→in_progress and `auto_complete_wo`
// may have completed the WO in the same call — the mutation invalidates the
// detail and this tab re-renders from the re-fetched truth.

import type { TFunction } from "i18next";
import { useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { CheckboxField, Field } from "../../../../components/field";
import { StatusChip } from "../../../../components/status-chip";
import { RequestError } from "../../../../lib/request";
import { useTerm } from "../../../onboarding/hooks/terminology";
import {
  useRecordOperationResult,
  useScrapReasons,
  useReleaseQcHold,
  useSkipOperation,
  useWorkCenters,
  type OperationQtyReport,
  type OperationResultRequest,
  type OperationResultSerialCapture,
  type SerialLotCapture,
  type SerialLotDirection,
  type WorkOrderOperation,
} from "../hooks/production-queries";
import {
  formatQty,
  formatTimestamp,
  isValidQtyInput,
  localeLabel,
  productionChipVariant,
  productionErrorMessage,
} from "../lib/format";

/** Op states an office action can still touch (§3.2; the server re-checks with
 * OPERATION_NOT_ACTIONABLE). The WORK ORDER's own status arm lives with the
 * caller (`canSkip`/`canRecord` are legal-state × permission, the header rule
 * — spec §4 S2 note, 2026-08-14): a terminal order renders no actions column
 * at all, matching how a role without the codes is treated. */
const SKIPPABLE = new Set<WorkOrderOperation["status"]>(["queued", "ready"]);
const RECORDABLE = new Set<WorkOrderOperation["status"]>([
  "queued",
  "ready",
  "in_progress",
  "paused",
]);

export function OperationsTab({
  workOrderId,
  facilityId,
  operations,
  qtyReports,
  captures,
  defaultUom,
  canSkip,
  canRecord,
  canReleaseHold,
}: {
  workOrderId: string;
  facilityId: string;
  operations: readonly WorkOrderOperation[];
  qtyReports: readonly OperationQtyReport[];
  /** e3's §2.14 rows. Used here for ONE decision only: whether a step that
   * `requires_serial_capture` already HAS a produced capture, which is exactly
   * the other half of the server's §3.2 guard ("already exists for the op, or
   * supplied in this call"). Without it the form would demand a redundant row
   * from a planner keying the completion of a step the FLOOR already labelled. */
  captures: readonly SerialLotCapture[];
  /** The work order's own UoM — what a §2.14 capture is measured in unless the
   * traveler says otherwise (§4 S10's rule, applied to the office form). */
  defaultUom: string;
  /** e9 × the WO's status: permission AND a non-terminal order (a draft's
   * steps stay skippable — §3.2's own routing-tailoring example). */
  canSkip: boolean;
  /** e34 × the WO's status: permission AND a released/in-progress order —
   * the endpoint's own arm, so the form is never offered where it can
   * only 409 (spec §4 S2 note, 2026-08-14). */
  canRecord: boolean;
  /**
   * `production.qc_hold.release` — e32's OWN code, and NOT `canRecord`.
   *
   * The release button used to live inside the `canRecord` actions cell, which
   * meant the one role §7 names for a quality decision could not click it: `qc`
   * holds `production.qc_hold.release` and does NOT hold
   * `production.operation.record`. A qc inspector saw the hold badge, no button,
   * and succeeded on the API from the same session (adversarial review,
   * 2026-08-07). The whole argument for shipping e32 in this slice is that a
   * hold with no release is a state a factory can be put into and cannot be got
   * out of — which was false for exactly the role that would need it.
   */
  canReleaseHold: boolean;
}) {
  const { t, i18n } = useTranslation();
  const { term } = useTerm();
  const workCenters = useWorkCenters(facilityId);
  const wcName = useMemo(() => {
    const map = new Map<string, string>();
    for (const wc of workCenters.data?.items ?? []) {
      map.set(wc.id, localeLabel(wc.name_i18n, i18n.language));
    }
    return map;
  }, [workCenters.data, i18n.language]);
  // The same catalog the record form offers, so the reports panel shows the
  // same words the operator picked (RQ dedupes the fetch with the form's).
  const scrapReasons = useScrapReasons();
  const reasonLabel = (code: string): string | undefined => {
    const cause = (scrapReasons.data ?? []).find((c) => c.code === code);
    return cause === undefined ? undefined : localeLabel(cause.name_i18n, i18n.language);
  };

  const seqByOpId = useMemo(() => {
    const map = new Map<string, number>();
    for (const op of operations) map.set(op.id, op.seq_no);
    return map;
  }, [operations]);

  const [open, setOpen] = useState<
    { opId: string; kind: "skip" | "record" | "qc_hold" } | undefined
  >(undefined);

  /** Op ids that already carry a PRODUCED §2.14 capture. `consumed` is excluded
   * deliberately — §3.2 says a drum that went IN proves nothing about the
   * bobbin that came out, so a consumed row must not relax the form either. */
  const opsWithProducedCapture = useMemo(() => {
    const ids = new Set<string>();
    for (const row of captures) {
      if (row.direction === "produced") ids.add(row.wo_operation_id);
    }
    return ids;
  }, [captures]);

  /** Op ids that already carry a §2.4 quantity report, from either capture
   * path — the server's §3.2 completion evidence, and what lets e34 complete
   * a step with no new quantity (D-299 owner ruling W1). */
  const opsWithQtyReport = useMemo(
    () => new Set(qtyReports.map((report) => report.wo_operation_id)),
    [qtyReports],
  );

  const showActions = canSkip || canRecord || canReleaseHold;

  // ---- the step list (§3.3 DataTable) --------------------------------------
  const opColumns: DataTableColumn<WorkOrderOperation>[] = [
    { key: "seq_no", header: t("production.detail.ops.colSeq"), id: true },
    {
      key: "name",
      header: t("production.detail.ops.colName"),
      render: (op) => (
        <>
          {op.name}
          {/* 04-C-5's two §2.2/§3.2 states, WORDED on the row rather than left
              to be inferred from a status chip that cannot express them. A
              re-opened step reads `in_progress` exactly like a step running for
              the first time, and a held step reads `in_progress` while every
              floor tap on it is being refused — both are numbers a planner
              would otherwise have to guess at. */}
          {op.rework_reopened_at !== null ? (
            <span className="production-badge" data-testid={`op-rework-${String(op.seq_no)}`}>
              {t("production.detail.ops.reworkBadge")}
            </span>
          ) : null}
          {op.qc_hold ? (
            <span className="production-badge is-warn" data-testid={`op-hold-${String(op.seq_no)}`}>
              {t("production.detail.ops.qcHoldBadge")}
            </span>
          ) : null}
          {op.status === "skipped" && op.skip_reason !== null ? (
            <span className="production-hint">
              {" "}
              {t("production.detail.ops.skippedReason", { reason: op.skip_reason })}
            </span>
          ) : null}
        </>
      ),
    },
    {
      key: "work_center_id",
      header: term("work_center"),
      render: (op) => wcName.get(op.work_center_id) ?? "",
    },
    {
      key: "status",
      header: t("production.detail.ops.colStatus"),
      render: (op) => (
        <StatusChip variant={productionChipVariant(op.status)} className="status-chip">
          {t(`production.status.op.${op.status}`)}
        </StatusChip>
      ),
    },
    {
      key: "qty",
      header: t("production.detail.ops.colQty"),
      numeric: true,
      render: (op) =>
        `${formatQty(op.qty_good, { locale: i18n.language })} / ${formatQty(op.qty_scrap, { locale: i18n.language })}`,
    },
    {
      key: "setup_minutes",
      header: t("production.detail.ops.colSetupMin"),
      render: (op) =>
        `${t("production.detail.ops.colPlanned")} ${String(op.setup_minutes_planned)} · ${t("production.detail.ops.colActual")} ${String(op.setup_minutes_actual)}`,
    },
    {
      key: "run_minutes",
      header: t("production.detail.ops.colRunMin"),
      render: (op) =>
        `${t("production.detail.ops.colPlanned")} ${formatQty(op.run_minutes_per_unit_planned, { locale: i18n.language, maxDp: 4 })} · ${t("production.detail.ops.colActual")} ${String(op.run_minutes_actual)}`,
    },
    {
      key: "machine_minutes_actual",
      header: t("production.detail.ops.colMachineMin"),
      numeric: true,
    },
    // The actions column exists for ANY of the three gates — a `qc` principal
    // has no record/skip button and must still reach the hold release. On a
    // TERMINAL work order the caller passes all three false, so the column is
    // absent exactly as it is for a role without the codes; the header's
    // status chip (and cancel reason) already say why.
    ...(showActions
      ? [
          {
            key: "actions",
            header: t("production.common.actions"),
            actions: true,
            render: (op: WorkOrderOperation) => {
              const kind = open?.opId === op.id ? open.kind : undefined;
              const onOpen = (next: "skip" | "record" | "qc_hold" | undefined): void => {
                setOpen(next === undefined ? undefined : { opId: op.id, kind: next });
              };
              return (
                <div className="wo-transitions row-actions">
                  {canSkip && SKIPPABLE.has(op.status) ? (
                    <Button
                      onClick={() => {
                        onOpen(kind === "skip" ? undefined : "skip");
                      }}
                    >
                      {t("production.detail.ops.skip")}
                    </Button>
                  ) : null}
                  {canRecord && RECORDABLE.has(op.status) ? (
                    <Button
                      onClick={() => {
                        onOpen(kind === "record" ? undefined : "record");
                      }}
                    >
                      {t("production.detail.ops.record")}
                    </Button>
                  ) : null}
                  {/* §5 e32 (04-C-5). Rendered ONLY on a held operation — module
                      07 is the hold's only writer and does not exist (D-071), so
                      this button is invisible on every current screen. That is
                      the honest state: a permanently-disabled control would
                      promise a capability, and an always-visible one would 409
                      every click. It ships WITH the guard it releases, which is
                      the whole of D-071's ruling — a hold with no release is a
                      state a factory can be put into and cannot be got out of.

                      GATED ON e32's OWN CODE, never on `canRecord`: `qc` — the
                      one role §7 names for this decision — holds
                      `production.qc_hold.release` and does NOT hold
                      `production.operation.record`, so riding the record gate
                      made the sentence above false for exactly the role that
                      needs it. */}
                  {canReleaseHold && op.qc_hold ? (
                    <Button
                      data-testid={`op-release-hold-${String(op.seq_no)}`}
                      onClick={() => {
                        onOpen(kind === "qc_hold" ? undefined : "qc_hold");
                      }}
                    >
                      {t("production.detail.ops.releaseHold")}
                    </Button>
                  ) : null}
                </div>
              );
            },
          },
        ]
      : []),
  ];

  /** The open form, attached to the step it acts on — the §3.3 `expansion`
   * contract. This tab is the reason that prop exists: with eight steps listed,
   * a skip / record-result / hold-release form floating below the table is a
   * form whose subject the reader has to reconstruct. */
  const opExpansion = (op: WorkOrderOperation): ReactNode => {
    if (open?.opId !== op.id) return null;
    const close = (): void => {
      setOpen(undefined);
    };
    switch (open.kind) {
      case "qc_hold":
        return <QcHoldReleaseForm workOrderId={workOrderId} opId={op.id} onDone={close} />;
      case "skip":
        return <SkipForm workOrderId={workOrderId} opId={op.id} onDone={close} />;
      case "record":
        return (
          <RecordResultForm
            workOrderId={workOrderId}
            opId={op.id}
            requiresMeasuredCapture={op.requires_measured_capture}
            requiresSerialCapture={op.requires_serial_capture}
            hasProducedCapture={opsWithProducedCapture.has(op.id)}
            hasQtyReport={opsWithQtyReport.has(op.id)}
            defaultUom={defaultUom}
            onDone={close}
          />
        );
    }
  };

  // ---- the recorded-results panel (§3.11) ----------------------------------
  const reportColumns: DataTableColumn<OperationQtyReport>[] = [
    {
      key: "seq_no",
      header: t("production.detail.ops.colSeq"),
      id: true,
      render: (report) => seqByOpId.get(report.wo_operation_id) ?? "",
    },
    {
      key: "qty",
      header: t("production.detail.ops.colQty"),
      numeric: true,
      render: (report) =>
        `${formatQty(report.qty_good, { locale: i18n.language })} / ${formatQty(report.qty_scrap, { locale: i18n.language })}`,
    },
    {
      key: "scrap_reason_code",
      header: t("production.detail.ops.scrapReason"),
      // The localized cause name, as the record form shows it — the catalog is
      // already fetched; a raw MACHINE_DRIFT here while the form says "Machine
      // drift" was an inconsistency, not a choice. Falls back to the code for a
      // cause retired since the report was keyed.
      render: (report) =>
        report.scrap_reason_code === null
          ? t("production.common.notAvailable")
          : (reasonLabel(report.scrap_reason_code) ?? report.scrap_reason_code),
    },
    {
      // The bare uuid, by design — never resolved to a name.
      key: "worker_id",
      header: term("operator"),
      render: (report) => <code>{report.worker_id}</code>,
    },
    {
      key: "capture_source",
      header: t("production.detail.ops.colSource"),
      render: (report) => t(`production.captureSource.${report.capture_source}`),
    },
    {
      key: "reported_at",
      header: t("production.detail.ops.colAt"),
      age: true,
      render: (report) => formatTimestamp(report.reported_at, i18n.language),
    },
  ];

  return (
    // `wo-panel-*`, not `*-tab`: these wrappers are the tab's PANEL, and the
    // strip above them is the §3.10 `Tabs` primitive. Naming a panel after a
    // tab is what gate check 3a ratchets away — it cannot tell a hand-rolled
    // tab control from a panel that borrowed its word.
    <div className="wo-panel-operations">
      <DataTable
        columns={opColumns}
        rows={operations}
        rowKey={(op) => op.id}
        empty={null}
        caption={t("production.detail.tab.operations")}
        rowTestId="op-row"
        rowProps={(op) => ({ "data-testid": `op-row-${String(op.seq_no)}` })}
        expansion={opExpansion}
        expansionLabel={(op) =>
          open?.opId === op.id
            ? open.kind === "qc_hold"
              ? t("production.detail.ops.releaseHoldTitle")
              : open.kind === "skip"
                ? t("production.detail.ops.skipTitle")
                : t("production.detail.ops.recordTitle")
            : undefined
        }
      />

      <h3>{t("production.detail.ops.reportsTitle")}</h3>
      {qtyReports.length === 0 ? (
        <p className="production-hint">{t("production.detail.ops.reportsEmpty")}</p>
      ) : (
        <DataTable
          columns={reportColumns}
          rows={qtyReports}
          rowKey={(report) => report.id}
          empty={null}
          rowProps={(report) => ({ "data-testid": `qty-report-${report.id}` })}
        />
      )}
    </div>
  );
}

/**
 * §5 e32 — the manual quality-hold release. The NOTE is required and is the
 * point of the form: this is a person overriding a quality decision, and the
 * server writes it into the hash-chained audit log, where an override has to be
 * findable forever.
 *
 * Nothing in this release can SET the hold (module 07 is its only writer —
 * D-071), so this form is unreachable in production today. It ships anyway,
 * with the guard: shipping a hold without its release would leave a factory
 * with an operation nobody can advance and no control anywhere in the product
 * to clear it — which is exactly the deadlock D-084 records for the
 * measured-capture guard, found on deployed staging rather than in review.
 */
function QcHoldReleaseForm({
  workOrderId,
  opId,
  onDone,
}: {
  workOrderId: string;
  opId: string;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [note, setNote] = useState("");
  const release = useReleaseQcHold();
  return (
    <form
      className="production-inline-form"
      aria-label={t("production.detail.ops.releaseHoldTitle")}
      data-testid="qc-hold-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (note.trim() === "") return;
        release.mutate({ workOrderId, opId, note: note.trim() }, { onSuccess: onDone });
      }}
    >
      <h4>{t("production.detail.ops.releaseHoldTitle")}</h4>
      <p className="production-hint">{t("production.detail.ops.releaseHoldHint")}</p>
      {/* §3.10 Field: same label-wraps-control association the bare <label>
          had; the testid stays on the input. */}
      <Field label={t("production.detail.ops.releaseHoldNote")}>
        <input
          type="text"
          required
          data-testid="qc-hold-note"
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
          }}
        />
      </Field>
      {/* THE ERROR STATE, which this form shipped without while both of its
          siblings in this file had one. A 409 `QC_HOLD_NOT_HELD` (module 07
          released it from under you), a 403 or a dropped connection left the
          form open and the button re-enabled with nothing on screen — the
          operator's only signal that the override did not happen would have
          been the badge still being there. */}
      {release.isError ? (
        <p className="form-error" role="alert" data-testid="qc-hold-error">
          {productionErrorMessage(t, release.error)}
        </p>
      ) : null}
      {/* type="submit" explicitly — Button defaults to type="button" and this
          control submits the release <form>. */}
      <Button
        type="submit"
        data-testid="qc-hold-submit"
        busy={release.isPending}
        busyLabel={t("production.common.saving")}
      >
        {t("production.detail.ops.releaseHold")}
      </Button>
      <Button onClick={onDone}>{t("production.common.cancel")}</Button>
    </form>
  );
}

function SkipForm({
  workOrderId,
  opId,
  onDone,
}: {
  workOrderId: string;
  opId: string;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const skip = useSkipOperation();
  const [reason, setReason] = useState("");

  return (
    <form
      aria-label={t("production.detail.ops.skipTitle")}
      onSubmit={(event) => {
        event.preventDefault();
        if (reason.trim().length === 0) return;
        skip.mutate({ workOrderId, opId, reason: reason.trim() }, { onSuccess: onDone });
      }}
    >
      <h4>{t("production.detail.ops.skipTitle")}</h4>
      <p className="production-hint">{t("production.detail.ops.skipHint")}</p>
      <label className="filter-chip">
        <span className="filter-chip-label">{t("production.detail.ops.skipReason")}</span>
        <input
          value={reason}
          required
          onChange={(event) => {
            setReason(event.target.value);
          }}
        />
      </label>
      {skip.isError ? (
        <p className="form-error" role="alert">
          {productionErrorMessage(t, skip.error)}
        </p>
      ) : null}
      <div className="form-actions wo-transitions">
        <Button type="submit" variant="primary" busy={skip.isPending}>
          {t("production.detail.ops.skip")}
        </Button>
        <Button onClick={onDone}>{t("production.common.cancel")}</Button>
      </div>
    </form>
  );
}

const MEASUREMENT_METHODS = ["scale_weight", "meter_counter", "manual_tape"] as const;

const SERIAL_DIRECTIONS = ["produced", "consumed"] as const;

/** One row of the §3.3 step-8 block — the office half of a §2.14 capture. The
 * `key` is a render key only; nothing about it is posted. */
interface SerialRow {
  readonly key: string;
  direction: SerialLotDirection;
  scanCode: string;
  serialNo: string;
  qty: string;
  uom: string;
}

const blankSerialRow = (key: string, uom: string): SerialRow => ({
  key,
  direction: "produced",
  scanCode: "",
  serialNo: "",
  qty: "",
  uom,
});

/** A row nobody has touched. The block always renders one, so "the planner
 * recorded a PARTIAL result and never opened the traveler's serial column"
 * must be distinguishable from "the planner started a row and left it broken"
 * — the first posts nothing, the second is refused by name. The UNIT is
 * excluded from the test on purpose: it arrives prefilled, so counting it
 * would make every row look touched. */
const serialRowTouched = (row: SerialRow): boolean =>
  row.scanCode.trim().length > 0 || row.serialNo.trim().length > 0 || row.qty.trim().length > 0;

/** The client half of §2.14's own rules, in the order a reader fixes them.
 * Returns the i18n leaf under `production.detail.ops.serialProblem`, or
 * undefined when the row is postable. */
function serialRowProblem(row: SerialRow): string | undefined {
  if (row.scanCode.trim().length === 0 && row.serialNo.trim().length === 0) {
    return "nothing_identified";
  }
  if (row.qty.trim().length === 0) return "qty_required";
  if (!isValidQtyInput(row.qty, { positive: true })) return "qty_invalid";
  if (row.uom.trim().length === 0) return "uom_required";
  return undefined;
}

/** §3.11's office record form: posts the EXACT §5 row 34 body. Scrap reason is
 * client-required when qty_scrap > 0 — the same rule the server enforces with
 * 422 SCRAP_REASON_REQUIRED.
 *
 * THE MEASURED-BARREL BLOCK (D-084) renders ONLY when the step
 * `requires_measured_capture`, because that is the only step whose paper
 * completion sheet carries those numbers (§3.3 step 7) — offering them on
 * every step would invite a guess. It is required only when the operator is
 * also COMPLETING the step, which is exactly the server's §3.2 guard: a
 * partial result without a barrel stays legal on both capture paths.
 *
 * THE SERIAL BLOCK (D-085 pt 3, added 2026-08-06) is the same shape of escape
 * for the same shape of guard, and the FIVE decisions it makes:
 *
 *  1. IT RENDERS ONLY ON A STEP THAT DEMANDS IT, like the barrel block above
 *     and UNLIKE the floor's S10 panel, which offers a collapsed capture on
 *     every step. The difference is deliberate: the floor operator is standing
 *     at the machine looking at a drum somebody engraved, so refusing the fact
 *     would throw away lineage nobody can reconstruct. The planner is
 *     transcribing yesterday's traveler for ONE step, and a capture block on a
 *     step whose sheet has no serial column invites an invented serial. The
 *     API still accepts captures on non-requiring steps (§5 row 34) — this is
 *     a form's choice about what to ASK for, never a narrowing of the contract.
 *  2. IT IS AN ARRAY, WITH ONE ROW ALREADY THERE. `serial_captures[]` is an
 *     array because a rolling step legitimately labels several drums in one
 *     traveler entry (D-085), and the common case is still one drum — so the
 *     first row costs no clicks and the second costs exactly one.
 *  3. THE DIRECTION CARRIES THE FLOOR'S OWN SENTENCES. "Came off this machine"
 *     / "Went into this machine" are the words S10 puts in front of the
 *     operator; a second vocabulary for the same fact would make the two
 *     halves of one physical event unrecognisable as the same thing. It is a
 *     `<select>` rather than S10's two big buttons because this form is keyed
 *     on a keyboard, not tapped with gloves — and the chosen option is restated
 *     underneath as a sentence, so its MEANING is on screen and not only its
 *     label.
 *  4. THE UNIT IS PREFILLED from the work order's own UoM, editable, never
 *     demanded — the S10 rule, applied here.
 *  5. IT BLOCKS ONLY WHAT THE SERVER WOULD BLOCK. §3.2 needs a PRODUCED
 *     capture that either already exists for the op or arrives in this call, so
 *     the refusal fires only when the operator is COMPLETING, the op has no
 *     produced capture yet, and no produced row was keyed. A partial result
 *     needs nothing; a step the FLOOR already labelled needs nothing; and a
 *     half-filled row is refused by NAME (which row, which field) rather than
 *     silently dropped. */
function RecordResultForm({
  workOrderId,
  opId,
  requiresMeasuredCapture,
  requiresSerialCapture,
  hasProducedCapture,
  hasQtyReport,
  defaultUom,
  onDone,
}: {
  workOrderId: string;
  opId: string;
  requiresMeasuredCapture: boolean;
  requiresSerialCapture: boolean;
  hasProducedCapture: boolean;
  /** The step already carries a quantity report (D-299 owner ruling W1). */
  hasQtyReport: boolean;
  defaultUom: string;
  onDone: () => void;
}) {
  const { t, i18n } = useTranslation();
  const record = useRecordOperationResult();
  const reasons = useScrapReasons();

  const [qtyGood, setQtyGood] = useState("0");
  const [qtyScrap, setQtyScrap] = useState("0");
  const [scrapReason, setScrapReason] = useState("");
  const [startedAt, setStartedAt] = useState("");
  const [endedAt, setEndedAt] = useState("");
  const [complete, setComplete] = useState(false);
  const [note, setNote] = useState("");
  const [batchLabel, setBatchLabel] = useState("");
  const [claimedQty, setClaimedQty] = useState("");
  const [measuredQty, setMeasuredQty] = useState("");
  const [calUom, setCalUom] = useState("");
  const [calMethod, setCalMethod] = useState<(typeof MEASUREMENT_METHODS)[number]>("meter_counter");
  const [calScan, setCalScan] = useState("");
  const [validationKey, setValidationKey] = useState<string | undefined>(undefined);
  const [serialRows, setSerialRows] = useState<SerialRow[]>(() => [
    blankSerialRow("serial-0", defaultUom),
  ]);
  const [nextSerialKey, setNextSerialKey] = useState(1);
  /** Which row is broken and how. Rendered INSIDE that row, because "row 2 is
   * missing its quantity" is only actionable next to row 2. */
  const [serialProblem, setSerialProblem] = useState<
    { index: number; problem: string } | undefined
  >(undefined);

  const patchSerialRow = (index: number, patch: Partial<SerialRow>): void => {
    setSerialRows((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const submit = () => {
    // D-299 owner ruling W1: completing a step that already carries a report
    // needs no new quantity, so there an empty field means zero, which e34
    // accepts. Anywhere else an empty field is still refused here, and a
    // result of zero and zero is still refused by e34.
    const noNewQtyNeeded = complete && hasQtyReport;
    const good = qtyGood.trim() === "" && noNewQtyNeeded ? "0" : qtyGood.trim();
    const scrap = qtyScrap.trim() === "" && noNewQtyNeeded ? "0" : qtyScrap.trim();
    if (
      !isValidQtyInput(good, { positive: false }) ||
      !isValidQtyInput(scrap, { positive: false })
    ) {
      setValidationKey("production.detail.ops.qtyInvalid");
      return;
    }
    const scrapNonZero = scrap !== "0" && Number(scrap) > 0;
    if (scrapNonZero && scrapReason.length === 0) {
      setValidationKey("production.detail.ops.scrapReasonRequired");
      return;
    }
    // A partially-filled barrel block is never sent: either all four required
    // fields or none. Half a measurement is not a measurement.
    const calFilled =
      batchLabel.trim().length > 0 ||
      claimedQty.trim().length > 0 ||
      measuredQty.trim().length > 0 ||
      calUom.trim().length > 0;
    const calComplete =
      batchLabel.trim().length > 0 &&
      claimedQty.trim().length > 0 &&
      measuredQty.trim().length > 0 &&
      calUom.trim().length > 0;
    if (requiresMeasuredCapture && (complete || calFilled) && !calComplete) {
      setValidationKey("production.detail.ops.calRequired");
      return;
    }
    if (
      calComplete &&
      (!isValidQtyInput(claimedQty, { positive: true }) ||
        !isValidQtyInput(measuredQty, { positive: true }))
    ) {
      setValidationKey("production.detail.ops.calQtyInvalid");
      return;
    }
    // ---- §3.3 step 8: the traveler's serials (D-085 pt 3) ----------------
    // Untouched rows are not "empty captures", they are rows the planner never
    // used — they are dropped, never posted as a hole.
    const touched = serialRows
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => serialRowTouched(row));
    const broken = touched.find(({ row }) => serialRowProblem(row) !== undefined);
    if (broken !== undefined) {
      setValidationKey(undefined);
      setSerialProblem({
        index: broken.index,
        problem: serialRowProblem(broken.row) ?? "nothing_identified",
      });
      return;
    }
    // The client half of the §3.2 serial arm, in the server's own terms: on
    // completion a PRODUCED capture must either already exist for this op or
    // arrive in this call. Both halves matter — demanding a row from a planner
    // completing a step the FLOOR already labelled would be a second deadlock,
    // in the opposite direction from the one D-085 exists to prevent.
    const producedKeyed = touched.some(({ row }) => row.direction === "produced");
    if (requiresSerialCapture && complete && !hasProducedCapture && !producedKeyed) {
      setSerialProblem(undefined);
      setValidationKey("production.detail.ops.serialRequired");
      return;
    }
    setSerialProblem(undefined);
    setValidationKey(undefined);
    const body: OperationResultRequest = {
      qty_good: good,
      qty_scrap: scrap,
      complete,
    };
    if (scrapReason.length > 0) body.scrap_reason_code = scrapReason;
    if (startedAt.length > 0) body.started_at = new Date(startedAt).toISOString();
    if (endedAt.length > 0) body.ended_at = new Date(endedAt).toISOString();
    if (note.trim().length > 0) body.note = note.trim();
    if (calComplete) {
      body.calibration = {
        batch_label: batchLabel.trim(),
        machine_claimed_qty: claimedQty.trim(),
        measured_qty: measuredQty.trim(),
        uom: calUom.trim(),
        measurement_method: calMethod,
        ...(calScan.trim().length > 0 ? { output_stock_unit_scan_code: calScan.trim() } : {}),
      };
    }
    if (touched.length > 0) {
      body.serial_captures = touched.map(({ row }): OperationResultSerialCapture => ({
        direction: row.direction,
        ...(row.serialNo.trim().length > 0 ? { serial_no: row.serialNo.trim() } : {}),
        ...(row.scanCode.trim().length > 0 ? { scan_code: row.scanCode.trim() } : {}),
        qty: row.qty.trim(),
        uom: row.uom.trim(),
      }));
    }
    record.mutate({ workOrderId, opId, body }, { onSuccess: onDone });
  };

  return (
    <form
      aria-label={t("production.detail.ops.recordTitle")}
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <h4>{t("production.detail.ops.recordTitle")}</h4>
      <label className="filter-chip">
        <span className="filter-chip-label">{t("production.detail.ops.qtyGood")}</span>
        <input
          value={qtyGood}
          inputMode="decimal"
          onChange={(event) => {
            setQtyGood(event.target.value);
          }}
        />
      </label>
      <label className="filter-chip">
        <span className="filter-chip-label">{t("production.detail.ops.qtyScrap")}</span>
        <input
          value={qtyScrap}
          inputMode="decimal"
          onChange={(event) => {
            setQtyScrap(event.target.value);
          }}
        />
      </label>
      <label className="filter-chip">
        <span className="filter-chip-label">{t("production.detail.ops.scrapReason")}</span>
        <select
          value={scrapReason}
          onChange={(event) => {
            setScrapReason(event.target.value);
          }}
        >
          <option value="">{t("production.detail.ops.pickReason")}</option>
          {(reasons.data ?? []).map((cause) => (
            <option key={cause.id} value={cause.code}>
              {localeLabel(cause.name_i18n, i18n.language)}
            </option>
          ))}
        </select>
      </label>
      <label className="filter-chip">
        <span className="filter-chip-label">{t("production.detail.ops.startedAt")}</span>
        <input
          type="datetime-local"
          value={startedAt}
          onChange={(event) => {
            setStartedAt(event.target.value);
          }}
        />
      </label>
      <label className="filter-chip">
        <span className="filter-chip-label">{t("production.detail.ops.endedAt")}</span>
        <input
          type="datetime-local"
          value={endedAt}
          onChange={(event) => {
            setEndedAt(event.target.value);
          }}
        />
      </label>
      <p className="production-hint">{t("production.detail.ops.timesHint")}</p>
      {requiresMeasuredCapture ? (
        <fieldset className="wo-calibration-fields" data-testid="record-calibration">
          <legend>{t("production.detail.ops.calTitle")}</legend>
          <p className="production-hint">{t("production.detail.ops.calHint")}</p>
          <label className="filter-chip">
            <span className="filter-chip-label">{t("production.detail.ops.calBatch")}</span>
            <input
              value={batchLabel}
              onChange={(event) => {
                setBatchLabel(event.target.value);
              }}
            />
          </label>
          <label className="filter-chip">
            <span className="filter-chip-label">{t("production.detail.ops.calClaimed")}</span>
            <input
              value={claimedQty}
              inputMode="decimal"
              onChange={(event) => {
                setClaimedQty(event.target.value);
              }}
            />
          </label>
          <label className="filter-chip">
            <span className="filter-chip-label">{t("production.detail.ops.calMeasured")}</span>
            <input
              value={measuredQty}
              inputMode="decimal"
              onChange={(event) => {
                setMeasuredQty(event.target.value);
              }}
            />
          </label>
          <label className="filter-chip">
            <span className="filter-chip-label">{t("production.detail.ops.calUom")}</span>
            <input
              value={calUom}
              onChange={(event) => {
                setCalUom(event.target.value);
              }}
            />
          </label>
          <label className="filter-chip">
            <span className="filter-chip-label">{t("production.detail.ops.calMethod")}</span>
            <select
              value={calMethod}
              onChange={(event) => {
                setCalMethod(event.target.value as (typeof MEASUREMENT_METHODS)[number]);
              }}
            >
              {MEASUREMENT_METHODS.map((method) => (
                <option key={method} value={method}>
                  {t(`production.calibration.method.${method}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="filter-chip">
            <span className="filter-chip-label">{t("production.detail.ops.calScan")}</span>
            <input
              value={calScan}
              onChange={(event) => {
                setCalScan(event.target.value);
              }}
            />
          </label>
          <p className="production-hint">{t("production.detail.ops.calScanHint")}</p>
        </fieldset>
      ) : null}
      {requiresSerialCapture ? (
        <fieldset className="wo-serial-fields" data-testid="record-serial-captures">
          <legend>{t("production.detail.ops.serialTitle")}</legend>
          <p className="production-hint">{t("production.detail.ops.serialHint")}</p>
          {hasProducedCapture ? (
            <p className="production-hint" data-testid="record-serial-already">
              {t("production.detail.ops.serialAlreadyCaptured")}
            </p>
          ) : null}
          {serialRows.map((row, index) => {
            const rowNo = index + 1;
            return (
              // A GROUP, not five loose fields. Every row repeats the same five
              // labels, so the row's own accessible name is what tells a screen
              // reader (and a test) which drum "Measured quantity" belongs to —
              // repeating "Labelled unit 2 —" inside five visible labels would
              // say the same thing four times too often.
              <div
                key={row.key}
                role="group"
                aria-label={t("production.detail.ops.serialRow", { index: rowNo })}
                className="wo-serial-row"
                data-testid={`record-serial-row-${String(index)}`}
              >
                <div className="wo-serial-row-head">
                  <strong>{t("production.detail.ops.serialRow", { index: rowNo })}</strong>
                  {/* Removable only while another row survives: the first row is
                      the single-drum case and must never need re-adding. */}
                  {serialRows.length > 1 ? (
                    <button
                      type="button"
                      className="wo-serial-remove"
                      aria-label={t("production.detail.ops.serialRemoveRow", { index: rowNo })}
                      data-testid={`record-serial-remove-${String(index)}`}
                      onClick={() => {
                        setSerialProblem(undefined);
                        setSerialRows((rows) => rows.filter((_, i) => i !== index));
                      }}
                    >
                      {t("production.detail.ops.serialRemove")}
                    </button>
                  ) : null}
                </div>
                <label className="filter-chip">
                  <span className="filter-chip-label">
                    {t("production.detail.ops.serialDirection")}
                  </span>
                  <select
                    value={row.direction}
                    data-testid={`record-serial-direction-${String(index)}`}
                    onChange={(event) => {
                      patchSerialRow(index, {
                        direction: event.target.value as SerialLotDirection,
                      });
                    }}
                  >
                    {SERIAL_DIRECTIONS.map((candidate) => (
                      <option key={candidate} value={candidate}>
                        {t(`production.detail.ops.serialDirections.${candidate}`)}
                      </option>
                    ))}
                  </select>
                </label>
                {/* The chosen direction restated as a sentence — the S10 rule:
                    the label says which button, the sentence says what it MEANS. */}
                <p className="production-hint" data-testid={`record-serial-note-${String(index)}`}>
                  {t(`production.detail.ops.serialDirectionNote.${row.direction}`)}
                </p>
                <label className="filter-chip">
                  <span className="filter-chip-label">{t("production.detail.ops.serialScan")}</span>
                  <input
                    value={row.scanCode}
                    autoComplete="off"
                    onChange={(event) => {
                      patchSerialRow(index, { scanCode: event.target.value });
                    }}
                  />
                </label>
                <label className="filter-chip">
                  <span className="filter-chip-label">{t("production.detail.ops.serialNo")}</span>
                  <input
                    value={row.serialNo}
                    autoComplete="off"
                    onChange={(event) => {
                      patchSerialRow(index, { serialNo: event.target.value });
                    }}
                  />
                </label>
                <label className="filter-chip">
                  <span className="filter-chip-label">{t("production.detail.ops.serialQty")}</span>
                  <input
                    value={row.qty}
                    inputMode="decimal"
                    onChange={(event) => {
                      patchSerialRow(index, { qty: event.target.value });
                    }}
                  />
                </label>
                <label className="filter-chip">
                  <span className="filter-chip-label">{t("production.detail.ops.serialUom")}</span>
                  <input
                    value={row.uom}
                    onChange={(event) => {
                      patchSerialRow(index, { uom: event.target.value });
                    }}
                  />
                </label>
                {serialProblem?.index === index ? (
                  <p
                    className="form-error"
                    role="alert"
                    data-testid={`record-serial-problem-${String(index)}`}
                  >
                    {t(`production.detail.ops.serialProblem.${serialProblem.problem}`, {
                      index: rowNo,
                    })}
                  </p>
                ) : null}
              </div>
            );
          })}
          <div className="form-actions">
            <button
              type="button"
              className="wo-serial-add"
              data-testid="record-serial-add"
              onClick={() => {
                setSerialRows((rows) => [
                  ...rows,
                  blankSerialRow(`serial-${String(nextSerialKey)}`, defaultUom),
                ]);
                setNextSerialKey((n) => n + 1);
              }}
            >
              {t("production.detail.ops.serialAdd")}
            </button>
          </div>
          <p className="production-hint">{t("production.detail.ops.serialAddHint")}</p>
        </fieldset>
      ) : null}
      {/* THE 1160 px CHECKBOX (surface review F6). A `.filter-chip` is a
          COLUMN with `align-items: stretch`, so this box grew to the width of
          the panel and drew its 13 px glyph in the middle of the row, nowhere
          near "Step finished". `CheckboxField` is the row form — box, then
          caption — and the box never stretches. The accessible name is
          unchanged, which is what M04-S13 and eight unit tests read. */}
      <CheckboxField
        label={t("production.detail.ops.complete")}
        checked={complete}
        onChange={setComplete}
      />
      <label className="filter-chip">
        <span className="filter-chip-label">{t("production.detail.ops.note")}</span>
        <input
          value={note}
          onChange={(event) => {
            setNote(event.target.value);
          }}
        />
      </label>
      {validationKey !== undefined ? (
        <p className="form-error" role="alert">
          {t(validationKey)}
        </p>
      ) : null}
      {record.isError ? (
        <p className="form-error" role="alert">
          {recordErrorMessage(t, record.error)}
        </p>
      ) : null}
      <div className="form-actions wo-transitions">
        <Button
          type="submit"
          variant="primary"
          busy={record.isPending}
          busyLabel={t("production.common.saving")}
        >
          {t("production.detail.ops.submit")}
        </Button>
        <Button onClick={onDone}>{t("production.common.cancel")}</Button>
      </div>
    </form>
  );
}

/**
 * The record-result form's refusal sentence. e34's `DOWNTIME_END_AT_INVALID`
 * (`before_window`, D-299 owner rulings S1 and S4) is worded for THIS form:
 * the reader stated the step's end, and completing the step would end its
 * open stoppage at that instant, more than 24 hours back. The general
 * sentence speaks of a stoppage's end time the reader never typed. Every
 * other refusal keeps the shared mapping.
 */
function recordErrorMessage(t: TFunction, error: unknown): string {
  if (
    error instanceof RequestError &&
    error.code === "DOWNTIME_END_AT_INVALID" &&
    (error.problem as Record<string, unknown> | undefined)?.["bound"] === "before_window"
  ) {
    return t("production.detail.ops.stoppageEndBeforeWindow");
  }
  return productionErrorMessage(t, error);
}
